import { useState, useEffect, useRef, useCallback } from 'react';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

interface RepoImage {
  image_id: string;
  url: string;
  language_code?: string;
  gender?: string;
  role?: string;
  location?: string;
  product?: string;
}

interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type ImagePickerSlot = 'cover_male' | 'cover_female' | `chapter_${number}`;

interface ImagePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (blob: Blob, slot: ImagePickerSlot) => void;
  slot: ImagePickerSlot;
  languageCode?: string;
}

const ASPECT_RATIOS: Record<string, { label: string; ratio: number }> = {
  cover: { label: 'Book Cover (3:4)', ratio: 3 / 4 },
  chapter: { label: 'Chapter Header (16:9)', ratio: 16 / 9 },
};

function getAspectForSlot(slot: ImagePickerSlot): { label: string; ratio: number } {
  if (slot.startsWith('chapter_')) return ASPECT_RATIOS.chapter!;
  return ASPECT_RATIOS.cover!;
}

export default function ImagePicker({ open, onClose, onSelect, slot, languageCode }: ImagePickerProps) {
  const [images, setImages] = useState<RepoImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [langFilter, setLangFilter] = useState<string>(languageCode || '');   // default to the product's language; '' = all
  const [seenLangs, setSeenLangs] = useState<string[]>([]);        // accumulates languages seen, to fill the dropdown
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<string>('');
  const [hidden, setHidden] = useState<Set<string>>(new Set());   // language-scoped discards
  const [bookOnly, setBookOnly] = useState(false);                 // show only book images (not dialog art)

  // Load the language's hidden-image list (discards apply to every book of the language).
  useEffect(() => {
    if (!open || !languageCode) return;
    fetch(`${BASE_URL}/api/admin/books/hidden-images?language_code=${encodeURIComponent(languageCode)}`)
      .then(r => r.ok ? r.json() : { hidden: [] })
      .then(d => setHidden(new Set(d.hidden || [])))
      .catch(() => { /* keep what we have */ });
  }, [open, languageCode]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Keep the language filter in sync with the product being edited.
  useEffect(() => { setLangFilter(languageCode || ''); }, [languageCode]);

  const discard = async (imageId: string) => {
    setHidden(prev => { const n = new Set(prev); n.add(imageId); return n; });   // optimistic
    if (!languageCode) return;
    try {
      await fetch(`${BASE_URL}/api/admin/books/hide-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: languageCode, image_id: imageId }),
      });
    } catch { /* keep optimistic */ }
  };
  const [selected, setSelected] = useState<RepoImage | null>(null);
  const [cropOffset, setCropOffset] = useState(0.5);
  const [cropping, setCropping] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const aspect = getAspectForSlot(slot);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Default to the WHOLE library (no language filter) so the picker is never empty;
      // narrow with the language dropdown when you want to. (Was hard-scoped to one language.)
      if (langFilter) params.set('language', langFilter);
      if (gender) params.set('gender', gender);
      if (search) params.set('search', search);
      const res = await fetch(`${BASE_URL}/api/admin/images/repository?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { images: RepoImage[] };
      setImages(data.images || []);
      setSeenLangs(prev => Array.from(new Set([...prev, ...(data.images || []).map(i => i.language_code).filter(Boolean) as string[]])).sort());
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [langFilter, gender, search]);

  useEffect(() => {
    if (open) {
      fetchImages();
      setSelected(null);
      setCropOffset(0.5);
    }
  }, [open, fetchImages]);

  const loadImage = useCallback((url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      // Image CDNs don't send CORS headers, so loading them directly onto a canvas
      // taints it and the crop preview comes up blank. Route the load through our
      // API's CORS-enabled image proxy (already-proxied / same-origin URLs pass through).
      img.src = url.startsWith(BASE_URL)
        ? url
        : `${BASE_URL}/api/admin/images/repository?proxyImage=${encodeURIComponent(url)}`;
    });
  }, []);

  const drawPreview = useCallback(async () => {
    if (!selected || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let img = imgRef.current;
    if (!img || img.src !== selected.url) {
      try {
        img = await loadImage(selected.url);
        imgRef.current = img;
      } catch {
        return;
      }
    }

    const crop = computeCrop(img.naturalWidth, img.naturalHeight, aspect.ratio, cropOffset);
    canvas.width = crop.w;
    canvas.height = crop.h;
    ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
  }, [selected, cropOffset, aspect.ratio, loadImage]);

  useEffect(() => {
    drawPreview();
  }, [drawPreview]);

  const handleConfirm = async () => {
    if (!selected || !canvasRef.current) return;
    setCropping(true);

    try {
      let img = imgRef.current;
      if (!img || img.src !== selected.url) {
        img = await loadImage(selected.url);
        imgRef.current = img;
      }

      const crop = computeCrop(img.naturalWidth, img.naturalHeight, aspect.ratio, cropOffset);
      const outCanvas = document.createElement('canvas');
      const maxDim = slot.startsWith('chapter_') ? 1200 : 800;
      const scale = Math.min(1, maxDim / Math.max(crop.w, crop.h));
      outCanvas.width = Math.round(crop.w * scale);
      outCanvas.height = Math.round(crop.h * scale);
      const octx = outCanvas.getContext('2d')!;
      octx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, outCanvas.width, outCanvas.height);

      outCanvas.toBlob(blob => {
        if (blob) onSelect(blob, slot);
        setCropping(false);
      }, 'image/jpeg', 0.92);
    } catch {
      setCropping(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-zinc-800 rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <h3 className="text-zinc-200 font-medium">
            Choose Image — {aspect.label}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-xl leading-none">&times;</button>
        </div>

        {!selected ? (
          <>
            <div className="p-3 border-b border-zinc-700 flex flex-wrap gap-2">
              <input
                type="text"
                placeholder="Search role, location…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchImages()}
                className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 text-sm w-full sm:w-48 border border-zinc-700 focus:border-teal-500 outline-none"
              />
              <select
                value={langFilter}
                onChange={e => setLangFilter(e.target.value)}
                className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 text-sm border border-zinc-700"
                title="Filter by language (defaults to the whole library)"
              >
                <option value="">All languages</option>
                {Array.from(new Set([languageCode, ...seenLangs].filter(Boolean) as string[])).sort().map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <select
                value={gender}
                onChange={e => setGender(e.target.value)}
                className="bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 text-sm border border-zinc-700"
              >
                <option value="">All genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <button
                onClick={fetchImages}
                className="px-3 py-1.5 rounded bg-teal-800 text-teal-200 text-sm hover:bg-teal-700 transition-colors"
              >
                Search
              </button>
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 ml-auto cursor-pointer select-none">
                <input type="checkbox" checked={bookOnly} onChange={e => setBookOnly(e.target.checked)} />
                Book images only
              </label>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {(() => {
                let visible = images.filter(i => !hidden.has(i.image_id));
                if (bookOnly) visible = visible.filter(i => i.product === 'books' || /^book-/.test(i.image_id || ''));
                if (loading) return <p className="text-zinc-500 text-sm text-center py-8">Loading images…</p>;
                if (visible.length === 0) return (
                  <p className="text-zinc-500 text-sm text-center py-8">
                    No images to show.{bookOnly ? ' No recovered/book images yet — click "Recover previous generations", or turn off "Book images only".' : hidden.size > 0 ? ` (${hidden.size} discarded for this language)` : ' Try a different search.'}
                  </p>
                );
                return (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                    {visible.map(img => (
                      <div
                        key={img.image_id}
                        className="group relative rounded overflow-hidden border-2 border-transparent hover:border-teal-500 transition-colors"
                      >
                        <button onClick={() => setSelected(img)} className="block w-full">
                          <img src={img.url} alt={img.role || 'image'} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-[10px] truncate">{img.role || img.location || img.image_id}</p>
                          </div>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); discard(img.image_id); }}
                          title="Discard — won't show again for any book of this language"
                          className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/60 text-white text-[11px] leading-none opacity-0 group-hover:opacity-100 hover:bg-red-700 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <p className="text-zinc-400 text-xs mb-2">
                  Adjust crop position — drag the slider to move the crop window
                </p>
                <canvas
                  ref={canvasRef}
                  className="rounded border border-zinc-700 max-w-full max-h-80 mx-auto block"
                />
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-zinc-500 text-xs">Top</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={cropOffset}
                    onChange={e => setCropOffset(parseFloat(e.target.value))}
                    className="flex-1 accent-teal-500"
                  />
                  <span className="text-zinc-500 text-xs">Bottom</span>
                </div>
              </div>

              <div className="md:w-48 flex flex-col gap-2">
                <div className="text-zinc-500 text-xs">
                  {selected.role && <p>Role: {selected.role}</p>}
                  {selected.location && <p>Location: {selected.location}</p>}
                  {selected.gender && <p>Gender: {selected.gender}</p>}
                </div>
                <button
                  onClick={handleConfirm}
                  disabled={cropping}
                  className="px-4 py-2 rounded bg-teal-700 text-teal-100 text-sm font-medium hover:bg-teal-600 disabled:opacity-50 transition-colors"
                >
                  {cropping ? 'Cropping…' : 'Use This Image'}
                </button>
                <button
                  onClick={() => { setSelected(null); imgRef.current = null; }}
                  className="px-4 py-2 rounded bg-zinc-700 text-zinc-300 text-sm hover:bg-zinc-600 transition-colors"
                >
                  Back to Gallery
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function computeCrop(imgW: number, imgH: number, targetRatio: number, offset: number): CropRect {
  const imgRatio = imgW / imgH;

  if (imgRatio > targetRatio) {
    const cropW = Math.round(imgH * targetRatio);
    const maxX = imgW - cropW;
    const x = Math.round(maxX * offset);
    return { x, y: 0, w: cropW, h: imgH };
  } else {
    const cropH = Math.round(imgW / targetRatio);
    const maxY = imgH - cropH;
    const y = Math.round(maxY * offset);
    return { x: 0, y, w: imgW, h: cropH };
  }
}
